import { useState, useEffect, useCallback, useLayoutEffect, useRef, memo } from 'react';
import ReactQuill from 'react-quill';

// Components
import Button from '~/components/CustomButton/CustomButton.jsx';
import PreviewContent from './PreviewContent';

// Elements
import { Input, Textarea } from '~/elements/Input';

import { EMAIL_EDITOR_MODULES, EMAIL_EDITOR_FORMATS, TRAILING_TOKEN, BEYOND_QUILL, getUnknownTokens, normalizeEmailHtml } from './templateUtils';

const MessageTab = memo(({ t, titleField, messageField, titleValue, messageValue, onChange, variables, isEmail, allowRawHtml }) => {
    const textareaElRef = useRef(null);
    const cursorPosRef = useRef(null);
    const pendingCursorRef = useRef(null);
    const quillRef = useRef(null);

    const titleElRef = useRef(null);
    const titleCursorRef = useRef(null);
    const pendingTitleCursorRef = useRef(null);

    const activeFieldRef = useRef('message');

    const [beyondQuill, setBeyondQuill] = useState(false);
    const editorChosenRef = useRef(false);

    useEffect(() => {
        if (editorChosenRef.current || !isEmail || !messageValue) return;
        editorChosenRef.current = true;
        setBeyondQuill(BEYOND_QUILL.test(messageValue));
    }, [isEmail, messageValue]);

    // Whoever can edit the html opens straight in it when the message needs it; everyone else
    // gets the rich editor, which is why message bodies are kept to plain content.
    const [override, setOverride] = useState(null);
    const mode = !isEmail
        ? 'plain'
        : override || (beyondQuill && allowRawHtml ? 'html' : 'quill');

    const useQuill = mode === 'quill';

    const value = messageValue || '';

    useLayoutEffect(() => {
        const restore = (elRef, pendingRef) => {
            const el = elRef.current;
            if (pendingRef.current === null || !el) return;
            const pos = pendingRef.current;
            pendingRef.current = null;
            el.focus();
            el.setSelectionRange(pos, pos);
        };
        restore(textareaElRef, pendingCursorRef);
        restore(titleElRef, pendingTitleCursorRef);
    });

    useEffect(() => {
        if (!useQuill) return;
        const editor = quillRef.current && quillRef.current.getEditor();
        if (!editor) return;
        editor.keyboard.addBinding({ key: 8 }, { collapsed: true }, (range) => {
            const textBefore = editor.getText(0, range.index);
            const match = textBefore.match(TRAILING_TOKEN);
            if (!match) return true;
            editor.deleteText(range.index - match[0].length, match[0].length, 'user');
            return false;
        });
    }, [useQuill]);

    const captureCursor = useCallback((e) => {
        activeFieldRef.current = 'message';
        textareaElRef.current = e.target;
        cursorPosRef.current = e.target.selectionStart;
    }, []);

    const captureTitleCursor = useCallback((e) => {
        activeFieldRef.current = 'title';
        titleElRef.current = e.target;
        titleCursorRef.current = e.target.selectionStart;
    }, []);

    const insertVariable = (v) => {
        const tag = `{{${v.key}}}`;

        if (activeFieldRef.current === 'title') {
            const title = titleValue || '';
            const pos = titleCursorRef.current !== null ? titleCursorRef.current : title.length;
            onChange(titleField, title.slice(0, pos) + tag + title.slice(pos));
            const next = pos + tag.length;
            titleCursorRef.current = next;
            pendingTitleCursorRef.current = next;
            return;
        }

        if (useQuill) {
            const editor = quillRef.current.getEditor();
            const range = editor.getSelection(true) || { index: editor.getLength(), length: 0 };
            editor.insertText(range.index, tag, 'user');
            editor.setSelection(range.index + tag.length, 0);
            return;
        }
        const pos = cursorPosRef.current !== null ? cursorPosRef.current : value.length;
        onChange(messageField, value.slice(0, pos) + tag + value.slice(pos));
        const next = pos + tag.length;
        cursorPosRef.current = next;
        pendingCursorRef.current = next;
    };

    const makeKeyDownHandler = (fieldName, field, text, cursorRef, pendingRef) => (e) => {
        activeFieldRef.current = fieldName;
        cursorRef.current = e.target.selectionStart;
        if (e.key !== 'Backspace') return;

        const { selectionStart: start, selectionEnd: end } = e.target;
        if (start !== end) return;

        const match = text.slice(0, start).match(TRAILING_TOKEN);
        if (!match) return;

        e.preventDefault();
        const newCursor = start - match[0].length;
        onChange(field, text.slice(0, newCursor) + text.slice(start));
        cursorRef.current = newCursor;
        pendingRef.current = newCursor;
    };

    const handleKeyDown = makeKeyDownHandler('message', messageField, value, cursorPosRef, pendingCursorRef);
    const handleTitleKeyDown = makeKeyDownHandler('title', titleField, titleValue || '', titleCursorRef, pendingTitleCursorRef);

    const hasVariables = Array.isArray(variables) && variables.length > 0;
    const unknownTokens = [...new Set([...getUnknownTokens(titleValue, variables), ...getUnknownTokens(value, variables)])];

    return (
        <div className="mt-3">
            <Input
                label={t('Title')}
                value={titleValue}
                onChange={(e) => onChange(titleField, e.target.value)}
                onFocus={captureTitleCursor}
                onKeyDown={handleTitleKeyDown}
                onKeyUp={captureTitleCursor}
                onClick={captureTitleCursor}
                onSelect={captureTitleCursor}
                className="mb-3"
            />
            {allowRawHtml && (
                <div className="d-flex justify-content-end mb-2">
                    <Button
                        sm
                        variant="outline-secondary"
                        type="button"
                        onClick={() => setOverride(mode === 'html' ? 'quill' : 'html')}
                    >
                        {mode === 'html' ? t('Use rich text editor') : t('Edit as HTML')}
                    </Button>
                </div>
            )}
            {mode === 'quill' && (
                <ReactQuill
                    ref={quillRef}
                    theme="snow"
                    value={value}
                    onChange={(html, delta, source) => { if (source === 'user') onChange(messageField, html); }}
                    onFocus={() => { activeFieldRef.current = 'message'; }}
                    modules={EMAIL_EDITOR_MODULES}
                    formats={EMAIL_EDITOR_FORMATS}
                    className="mb-2"
                />
            )}
            {(mode === 'plain' || mode === 'html') && (
                <Textarea
                    label={t('Message')}
                    value={value}
                    onChange={(e) => onChange(messageField, e.target.value)}
                    onKeyDown={handleKeyDown}
                    onKeyUp={captureCursor}
                    onClick={captureCursor}
                    onSelect={captureCursor}
                    className="mb-2"
                />
            )}

            {hasVariables && (
                <div className="mb-3">
                    <label className="control-label">{t('Click a placeholder to insert it into the template')} :</label>
                    <div className="d-flex flex-wrap gap-1 mt-2">
                        {variables.map((v) => (
                            <span
                                key={v.key}
                                className="placeholder-badge d-inline-block px-2 py-1 rounded-pill fw-semibold user-select-none"
                                title={`${v.label} — e.g. ${v.example}`}
                                onClick={() => insertVariable(v)}
                            >
                                {`{{${v.key}}}`}
                            </span>
                        ))}
                    </div>
                </div>
            )}

            {unknownTokens.length > 0 && (
                <div className="mb-2">
                    <small className="text-danger">
                        {t('Unknown placeholder')}: {unknownTokens.join(', ')}
                    </small>
                </div>
            )}

            {value && (
                <div className="bg-light rounded p-3">
                    <small className="text-muted fw-semibold d-block mb-1">{t('Preview')}</small>
                    <small className="text-pre-wrap">
                        <PreviewContent message={isEmail ? normalizeEmailHtml(value) : value} variables={variables} />
                    </small>
                </div>
            )}
        </div>
    );
});

export default MessageTab;
